import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [countersVisible, setCountersVisible] = useState(false);
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isVideoModal2Open, setIsVideoModal2Open] = useState(false);
  const [isVideoModal3Open, setIsVideoModal3Open] = useState(false);
  const countersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCountersVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (countersRef.current) {
      observer.observe(countersRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const investmentSelect = form.querySelector('select[name="investmentAmount"]') as HTMLSelectElement;
    const investmentAmount = investmentSelect.options[investmentSelect.selectedIndex].text;
    
    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('fullName', formData.get('fullName') as string);
    urlEncodedData.append('email', formData.get('email') as string);
    urlEncodedData.append('phone', formData.get('phone') as string);
    urlEncodedData.append('investmentAmount', investmentAmount);
    urlEncodedData.append('message', formData.get('message') as string);

    try {
      const response = await fetch('https://readdy.ai/api/form/d63070j0723ekv2it72g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const AnimatedCounter = ({ end, suffix = '', prefix = '' }: { end: number; suffix?: string; prefix?: string }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      if (!countersVisible) return;
      
      let start = 0;
      const duration = 2000;
      const increment = end / (duration / 16);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);
      
      return () => clearInterval(timer);
    }, [countersVisible, end]);
    
    return <span>{prefix}{count.toLocaleString()}{suffix}</span>;
  };

  const translations = {
    en: {
      nav: {
        home: 'Home',
        services: 'Services',
        investment: 'Investment',
        about: 'About',
        faq: 'FAQ',
        getStarted: 'Get Started'
      },
      hero: {
        badge: 'WEALTH CREATION EXPERTS',
        title1: 'MULTIPLY',
        title2: 'YOUR WEALTH',
        title3: 'WITH US',
        description: 'Transform your financial future with 20% annual returns. Real estate investments backed by physical assets, zero stock market risk.',
        startInvesting: 'Start Investing',
        seeReturns: 'See Returns',
        securityLabel: 'Security',
        securityValue: 'Notary Secured',
        assetsLabel: 'Assets',
        assetsValue: 'Physical Only',
        portfolioGrowth: 'Portfolio Growth',
        investmentReturns: 'Investment Returns',
        afterYears: 'after 5 years',
        year: 'Year',
        rickClub: 'Rick Club (20%)',
        bank: 'Bank (1.5%)',
        annualReturns: 'Annual Returns',
        assetsManaged: 'Assets Managed',
        scroll: 'SCROLL'
      },
      stats: {
        annualReturns: 'Annual Returns',
        marketRisk: 'Market Risk',
        notarySecured: 'Notary Secured',
        assetsManaged: 'Assets Managed'
      },
      services: {
        badge: 'WHY CHOOSE US',
        title: 'The',
        titleHighlight: 'Smart Way',
        titleEnd: 'to Invest',
        description: "We're not a bank and we don't work with Wall Street. What we do is more real, more secure, and more profitable.",
        realEstate: {
          title: 'Real Estate Focus',
          description: 'Our specialty is acquiring physical assets—land and houses—to create tangible wealth you can see and touch.',
          cta: 'Learn More'
        },
        returns: {
          title: '20%+ Returns',
          description: 'Enjoy returns exceeding 20% annually. Your money grows in record time with our proven investment strategies.',
          cta: 'View Plans'
        },
        zeroRisk: {
          title: 'Zero Risk',
          description: 'No stock market crashes. Everything is transparent with public notary supervision and banking oversight.',
          cta: 'Our Security'
        }
      },
      comparison: {
        badge: 'INVESTMENT COMPARISON',
        title: 'See The',
        titleHighlight: 'Difference',
        description: 'Compare traditional bank returns with Rick Club Investment and see why smart investors choose us',
        chartTitle: '5-Year Growth Comparison',
        traditionalBank: 'Traditional Bank',
        rickClubInvestment: 'Rick Club Investment',
        afterYears: 'after 5 years',
        profit: 'profit',
        moreProfit: 'More Profit',
        withRickClub: 'with Rick Club Investment',
        example: 'Example',
        bankFor5Years: 'Bank @ 1.5% for 5 years',
        rickClubFor5Years: 'Rick Club @ 20% for 5 years',
        totalEarnings: 'Total earnings',
        doubleYourMoney: 'Double your money!',
        startJourney: 'Start Your Investment Journey',
        watchVideo: 'Watch Video'
      },
      about: {
        badge: 'OUR MISSION',
        title: 'Creating',
        titleHighlight: 'Wealth',
        titleEnd: ', Building Trust',
        paragraph1: 'Rick Club Investment LLC is a company dedicated to the creation and management of wealth, serving our partners with excellence and integrity.',
        paragraph2: 'Our mission is to create investment opportunities for our partners and the general public, helping you achieve your financial goals and enjoy your retirement without worrying about money.',
        paragraph3: 'We are a boutique firm dedicated to serving all our partners by creating opportunities through asset acquisition and generating medium- and long-term returns. We have the key and the necessary tools to double every penny of our investors\' money.',
        stockMarketRisk: 'Stock Market Risk',
        notarySecured: 'Notary Secured',
        annualReturns: 'Annual Returns',
        trustedBy: 'Trusted by Investors',
        since: 'Since 2020'
      },
      future: {
        badge: 'YOUR PATH TO WEALTH',
        title: 'The',
        titleHighlight: 'Future',
        titleEnd: 'Is In Your Hands',
        paragraph1: "Let's talk about money, investment, retirement, and vacations. It sounds great, everyone wants something like that, but nobody knows how to achieve it because nobody taught you how.",
        paragraph2: "In school, they don't teach us about money; they only educate you to be an employee until you die, without rest or vacations.",
        paragraph3: "But that's what we're here for—to show you the way. We are the mastermind that illuminates your path.",
        straightToPoint: "Let's Get Straight to the Point",
        comparisonDescription: 'The differences between investing in a bank versus investing with Rick Club Investment',
        traditionalBank: 'Traditional Bank',
        annualReturns: 'Annual Returns',
        minimalGrowth: 'Minimal growth on your savings',
        yearsForReturns: 'Years to see any real returns',
        inflationLoss: 'Money loses value to inflation',
        rickClubInvestment: 'Rick Club Investment',
        doubleIn5Years: 'Double your money in 5 years',
        physicalAssets: 'Physical assets, zero market risk',
        notarySecured: 'Notary-secured investments',
        takeControl: 'Take Control of Your Future'
      },
      faq: {
        badge: 'FAQ',
        title: 'Common',
        titleHighlight: 'Questions',
        description: 'Everything you need to know about investing with us',
        questions: [
          {
            q: 'How can I earn $100,000 a year?',
            a: "With us, it's possible. When it comes to earnings, nobody does it better than us. You can find places that offer 5% or 7%, traditional banks with 1.5%, and Wall Street reaching 9%, but with a high risk of losing your money. With our 20% annual returns on physical asset investments, earning $100,000 a year is achievable with the right investment amount. For example, a $500,000 investment at 20% annual return generates $100,000 per year."
          },
          {
            q: 'What makes Rick Club Investment different from banks?',
            a: "We're not a bank, nor do we work with Wall Street. What we do is more real and secure. Our specialty is acquiring physical assets—land and houses—to create wealth. While banks offer 1.5% returns, we provide 20%+ annual returns. Your money is invested in tangible real estate, not volatile stocks, ensuring zero risk from market crashes."
          },
          {
            q: 'Is there any risk involved?',
            a: 'The risk is none. When you invest your money with Rick Club Investment, we ensure everything is transparent. A reputable public notary reviews everything meticulously, and your signature is not placed without the supervision of a banking agent and the seal of a public notary. This is to avoid risk and guarantee the security and trust of all our clients. Your investment is in physical assets, not the stock market.'
          },
          {
            q: 'What is the minimum investment amount?',
            a: "You don't need enormous amounts of money to become an active partner of Rick Club. Start with what you have; it's about taking the first step. We work with various investment levels, and our team can help you determine the best starting point based on your financial goals. Even starting with $100,000 can double to $200,000 in 5 years with our 20% annual returns."
          },
          {
            q: 'How long are the investment terms?',
            a: 'We offer medium- and long-term investment plans, typically ranging from 5 to 10 years. Our most popular plan is the 5-year term with 20% annual returns. We have long-term plans and projects where we help and advise you on when and how to invest, ensuring your money grows consistently over time without you having to lift a finger.'
          }
        ]
      },
      contact: {
        badge: 'GET IN TOUCH',
        title: "Let's Build Your",
        titleHighlight: 'Financial Future',
        description: "The key to success is starting today. Don't let doubt hold you back; time is ticking, and if you don't take that step, you'll never find success.",
        phone: 'Phone',
        email: 'Email',
        officeLocation: 'Office Location',
        location: '320 Deker Dr, Irving, TX 75062',
        scheduleConsultation: 'Schedule a Consultation',
        formDescription: "Fill out the form and our team will get back to you within 24 hours.",
        successMessage: 'Thank you! We\'ll contact you soon.',
        errorMessage: 'Something went wrong. Please try again.',
        fullName: 'Full Name',
        fullNamePlaceholder: 'John Doe',
        emailPlaceholder: 'john@example.com',
        phonePlaceholder: '(214) 815-4829',
        investmentAmount: 'Investment Amount',
        selectAmount: 'Select amount range',
        message: 'Message',
        messagePlaceholder: 'Tell us about your investment goals...',
        maxCharacters: 'Maximum 500 characters',
        submitting: 'Submitting...',
        submitButton: 'Schedule Consultation',
        privacyNotice: 'By submitting this form, you agree to our privacy policy and terms of service.'
      },
      footer: {
        description: 'Creating investment opportunities and building wealth for our partners through secure real estate investments.',
        company: 'Company',
        aboutUs: 'About Us',
        ourServices: 'Our Services',
        investmentPlans: 'Investment Plans',
        faq: 'FAQ',
        contact: 'Contact',
        services: 'Services',
        realEstateInvestment: 'Real Estate Investment',
        landAcquisition: 'Land Acquisition',
        propertyManagement: 'Property Management',
        wealthCreation: 'Wealth Creation',
        financialAdvisory: 'Financial Advisory',
        legal: 'Legal',
        privacyPolicy: 'Privacy Policy',
        copyright: '© 2025 Rick Club Investment LLC. All rights reserved.'
      }
    },
    es: {
      nav: {
        home: 'Inicio',
        services: 'Servicios',
        investment: 'Inversión',
        about: 'Nosotros',
        faq: 'Preguntas',
        getStarted: 'Comenzar'
      },
      hero: {
        badge: 'EXPERTOS EN CREACIÓN DE RIQUEZA',
        title1: 'MULTIPLICA',
        title2: 'TU RIQUEZA',
        title3: 'CON NOSOTROS',
        description: 'Transforma tu futuro financiero con retornos anuales del 20%. Inversiones inmobiliarias respaldadas por activos físicos, cero riesgo del mercado de valores.',
        startInvestinging: 'Comenzar a Invertir',
        seeReturns: 'Ver Retornos',
        securityLabel: 'Seguridad',
        securityValue: 'Notariado',
        assetsLabel: 'Activos',
        assetsValue: 'Solo Físicos',
        portfolioGrowth: 'Crecimiento del Portafolio',
        investmentReturns: 'Retornos de Inversión',
        afterYears: 'después de 5 años',
        year: 'Año',
        rickClub: 'Rick Club (20%)',
        bank: 'Banco (1.5%)',
        annualReturns: 'Retornos Anuales',
        assetsManaged: 'Activos Gestionados',
        scroll: 'DESPLAZAR'
      },
      stats: {
        annualReturns: 'Retornos Anuales',
        marketRisk: 'Riesgo de Mercado',
        notarySecured: 'Notariado',
        assetsManaged: 'Activos Gestionados'
      },
      services: {
        badge: 'POR QUÉ ELEGIRNOS',
        title: 'La',
        titleHighlight: 'Forma Inteligente',
        titleEnd: 'de Invertir',
        description: 'No somos un banco y no trabajamos con Wall Street. Lo que hacemos es más real, más seguro y más rentable.',
        realEstate: {
          title: 'Enfoque Inmobiliario',
          description: 'Nuestra especialidad es adquirir activos físicos—terrenos y casas—para crear riqueza tangible que puedes ver y tocar.',
          cta: 'Saber Más'
        },
        returns: {
          title: 'Retornos del 20%+',
          description: 'Disfruta de retornos superiores al 20% anual. Tu dinero crece en tiempo récord con nuestras estrategias de inversión probadas.',
          cta: 'Ver Planes'
        },
        zeroRisk: {
          title: 'Cero Riesgo',
          description: 'Sin caídas del mercado de valores. Todo es transparente con supervisión notarial pública y vigilancia bancaria.',
          cta: 'Nuestra Seguridad'
        }
      },
      comparison: {
        badge: 'COMPARACIÓN DE INVERSIONES',
        title: 'Ve La',
        titleHighlight: 'Diferencia',
        description: 'Compara los retornos bancarios tradicionales con Rick Club Investment y descubre por qué los inversores inteligentes nos eligen',
        chartTitle: 'Comparación de Crecimiento a 5 Años',
        traditionalBank: 'Banco Tradicional',
        rickClubInvestment: 'Rick Club Investment',
        afterYears: 'después de 5 años',
        profit: 'ganancia',
        moreProfit: 'Más Ganancia',
        withRickClub: 'con Rick Club Investment',
        example: 'Ejemplo',
        bankFor5Years: 'Banco @ 1.5% por 5 años',
        rickClubFor5Years: 'Rick Club @ 20% por 5 años',
        totalEarnings: 'Ganancias totales',
        doubleYourMoney: '¡Duplica tu dinero!',
        startJourney: 'Comienza Tu Viaje de Inversión',
        watchVideo: 'Ver Video'
      },
      about: {
        badge: 'NUESTRA MISIÓN',
        title: 'Creando',
        titleHighlight: 'Riqueza',
        titleEnd: ', Construyendo Confianza',
        paragraph1: 'Rick Club Investment LLC es una empresa dedicada a la creación y gestión de riqueza, sirviendo a nuestros socios con excelencia e integridad.',
        paragraph2: 'Nuestra misión es crear oportunidades de inversión para nuestros socios y el público en general, ayudándote a alcanzar tus metas financieras y disfrutar de tu jubilación sin preocuparte por el dinero.',
        paragraph3: 'Somos una firma boutique dedicada a servir a todos nuestros socios creando oportunidades a través de la adquisición de activos y generando retornos a mediano y largo plazo. Tenemos la clave y las herramientas necesarias para duplicar cada centavo del dinero de nuestros inversores.',
        stockMarketRisk: 'Riesgo de Mercado',
        notarySecured: 'Notariado',
        annualReturns: 'Retornos Anuales',
        trustedBy: 'Confiado por Inversores',
        since: 'Desde 2020'
      },
      future: {
        badge: 'TU CAMINO A LA RIQUEZA',
        title: 'El',
        titleHighlight: 'Futuro',
        titleEnd: 'Está En Tus Manos',
        paragraph1: 'Hablemos de dinero, inversión, jubilación y vacaciones. Suena genial, todos quieren algo así, pero nadie sabe cómo lograrlo porque nadie te enseñó cómo.',
        paragraph2: 'En la escuela no nos enseñan sobre el dinero; solo te educan para ser un empleado hasta que mueras, sin descanso ni vacaciones.',
        paragraph3: 'Pero para eso estamos aquí—para mostrarte el camino. Somos la mente maestra que ilumina tu sendero.',
        straightToPoint: 'Vayamos Directo al Grano',
        comparisonDescription: 'Las diferencias entre invertir en un banco versus invertir con Rick Club Investment',
        traditionalBank: 'Banco Tradicional',
        annualReturns: 'Retornos Anuales',
        minimalGrowth: 'Crecimiento mínimo en tus ahorros',
        yearsForReturns: 'Años para ver retornos reales',
        inflationLoss: 'El dinero pierde valor por inflación',
        rickClubInvestment: 'Rick Club Investment',
        doubleIn5Years: 'Duplica tu dinero en 5 años',
        physicalAssets: 'Activos físicos, cero riesgo de mercado',
        notarySecured: 'Inversiones not notariadas',
        takeControl: 'Toma Control de Tu Futuro'
      },
      faq: {
        badge: 'PREGUNTAS FRECUENTES',
        title: 'Preguntas',
        titleHighlight: 'Comunes',
        description: 'Todo lo que necesitas saber sobre invertir con nosotros',
        questions: [
          {
            q: '¿Cómo puedo ganar $100,000 al año?',
            a: 'Con nosotros, es posible. Cuando se trata de ganancias, nadie lo hace mejor que nosotros. Puedes encontrar lugares que ofrecen 5% o 7%, bancos tradicionales con 1.5%, y Wall Street alcanzando 9%, pero con un alto riesgo de perder tu dinero. Con nuestros retornos anuales del 20% en inversiones de activos físicos, ganar $100,000 al año es alcanzable con la cantidad de inversión correcta. Por ejemplo, una inversión de $500,000 al 20% de retorno anual genera $100,000 por año.'
          },
          {
            q: '¿Qué hace diferente a Rick Club Investment de los bancos?',
            a: 'No somos un banco, ni trabajamos con Wall Street. Lo que hacemos es más real y seguro. Nuestra especialidad es adquirir activos físicos—terrenos y casas—para crear riqueza. Mientras los bancos ofrecen retornos del 1.5%, nosotros proporcionamos retornos anuales del 20%+. Tu dinero se invierte en bienes raíces tangibles, no en acciones volátiles, asegurando cero riesgo de caídas del mercado.'
          },
          {
            q: '¿Hay algún riesgo involucrado?',
            a: 'El riesgo es nulo. Cuando inviertes tu dinero con Rick Club Investment, aseguramos que todo sea transparente. Un notario público de buena reputación revisa todo meticulosamente, y tu firma no se coloca sin la supervisión de un agente bancario y el sello de un notario público. Esto es para evitar riesgos y garantizar la seguridad y confianza de todos nuestros clientes. Tu inversión está en activos físicos, no en el mercado de valores.'
          },
          {
            q: '¿Cuál es el monto mínimo de inversión?',
            a: 'No necesitas cantidades enormes de dinero para convertirte en un socio activo de Rick Club. Comienza con lo que tienes; se trata de dar el primer paso. Trabajamos con varios niveles de inversión, y nuestro equipo puede ayudarte a determinar el mejor punto de partida según tus metas financieras. Incluso comenzando con $100,000 puede duplicarse a $200,000 en 5 años con nuestros retornos anuales del 20%.'
          },
          {
            q: '¿Cuánto duran los términos de inversión?',
            a: 'Ofrecemos planes de inversión a mediano y largo plazo, típicamente de 5 a 10 años. Nuestro plan más popular es el término de 5 años con retornos anuales del 20%. Tenemos planes y proyectos a largo plazo donde te ayudamos y asesoramos sobre cuándo y cómo invertir, asegurando que tu dinero crezca consistentemente con el tiempo sin que tengas que mover un dedo.'
          }
        ]
      },
      contact: {
        badge: 'CONTÁCTANOS',
        title: 'Construyamos Tu',
        titleHighlight: 'Futuro Financiero',
        description: 'La clave del éxito es comenzar hoy. No dejes que la duda te detenga; el tiempo corre, y si no das ese paso, nunca encontrarás el éxito.',
        phone: 'Teléfono',
        email: 'Correo',
        officeLocation: 'Ubicación de Oficina',
        location: '320 Deker Dr, Irving, TX 75062',
        scheduleConsultation: 'Agendar una Consulta',
        formDescription: 'Completa el formulario y nuestro equipo te contactará en 24 horas.',
        successMessage: '¡Gracias! Te contactaremos pronto.',
        errorMessage: 'Algo salió mal. Por favor intenta de nuevo.',
        fullName: 'Nombre Completo',
        fullNamePlaceholder: 'Juan Pérez',
        emailPlaceholder: 'juan@ejemplo.com',
        phonePlaceholder: '(214) 815-4829',
        investmentAmount: 'Monto de Inversión',
        selectAmount: 'Selecciona rango de monto',
        message: 'Mensaje',
        messagePlaceholder: 'Cuéntanos sobre tus metas de inversión...',
        maxCharacters: 'Máximo 500 caracteres',
        submitting: 'Enviando...',
        submitButton: 'Agendar Consulta',
        privacyNotice: 'Al enviar este formulario, aceptas nuestra política de privacidad y términos de servicio.'
      },
      footer: {
        description: 'Creando oportunidades de inversión y construyendo riqueza para nuestros socios a través de inversiones inmobiliarias seguras.',
        company: 'Empresa',
        aboutUs: 'Nosotros',
        ourServices: 'Nuestros Servicios',
        investmentPlans: 'Planes de Inversión',
        faq: 'Preguntas',
        contact: 'Contacto',
        services: 'Servicios',
        realEstateInvestment: 'Inversión Inmobiliaria',
        landAcquisition: 'Adquisición de Terrenos',
        propertyManagement: 'Gestión de Propiedades',
        wealthCreation: 'Creación de Riqueza',
        financialAdvisory: 'Asesoría Financiera',
        legal: 'Legal',
        privacyPolicy: 'Política de Privacidad',
        copyright: '© 2025 Rick Club Investment LLC. Todos los derechos reservados.'
      }
    }
  };

  const t = translations[language];

  return (
    <div className="min-h-screen bg-stone-50 font-body">
      {/* Video Modal */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setIsVideoModalOpen(false)}
          ></div>
          <div className="relative z-10 w-full max-w-4xl mx-4">
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-amber-400 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            <div className="bg-stone-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/osi6I1WhK8A?autoplay=1"
                  title="Investment Video"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal 2 */}
      {isVideoModal2Open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setIsVideoModal2Open(false)}
          ></div>
          <div className="relative z-10 w-full max-w-4xl mx-4">
            <button
              onClick={() => setIsVideoModal2Open(false)}
              className="absolute -top-12 right-0 text-white hover:text-amber-400 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            <div className="bg-stone-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/x713Y9zRFi4?autoplay=1"
                  title="Investment Video 2"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal 3 */}
      {isVideoModal3Open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setIsVideoModal3Open(false)}
          ></div>
          <div className="relative z-10 w-full max-w-4xl mx-4">
            <button
              onClick={() => setIsVideoModal3Open(false)}
              className="absolute -top-12 right-0 text-white hover:text-amber-400 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            <div className="bg-stone-900 rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-video">
                <iframe
                  src="https://www.youtube.com/embed/ZW4A4lrJEiA?autoplay=1"
                  title="Investment Video 3"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes drawLine {
          0% {
            stroke-dashoffset: 1000;
          }
          100% {
            stroke-dashoffset: 0;
          }
        }
        @keyframes moveLine {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: -50;
          }
        }
        @keyframes moveLineGold {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: -60;
          }
        }
        @keyframes pulseGlow {
          0%, 100% {
            filter: drop-shadow(0 0 8px rgba(212, 175, 55, 0.6));
          }
          50% {
            filter: drop-shadow(0 0 20px rgba(212, 175, 55, 0.9));
          }
        }
        .animated-line {
          stroke-dasharray: 10 5;
          animation: moveLine 1s linear infinite;
        }
        .animated-line-gold {
          stroke-dasharray: 15 8;
          animation: moveLineGold 0.8s linear infinite;
        }
        .animated-line-gray {
          stroke-dasharray: 8 4;
          animation: moveLine 1.2s linear infinite;
        }
        .badge-hover {
          transition: all 0.3s ease;
        }
        .badge-hover:hover {
          animation: badgePulse 0.6s ease-in-out infinite;
        }
        @keyframes badgePulse {
          0%, 100% {
            transform: scale(1) rotate(6deg);
            box-shadow: 0 25px 50px -12px rgba(212, 175, 55, 0.4);
          }
          50% {
            transform: scale(1.1) rotate(6deg);
            box-shadow: 0 35px 60px -12px rgba(212, 175, 55, 0.6);
          }
        }
        @keyframes badgePulseLeft {
          0%, 100% {
            transform: scale(1) rotate(-3deg);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          }
          50% {
            transform: scale(1.1) rotate(-3deg);
            box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.35);
          }
        }
        .badge-hover-left {
          transition: all 0.3s ease;
        }
        .badge-hover-left:hover {
          animation: badgePulseLeft 0.6s ease-in-out infinite;
        }
        @keyframes dataPointPulse {
          0%, 100% {
            r: 6;
            opacity: 1;
          }
          50% {
            r: 9;
            opacity: 0.8;
          }
        }
        .data-point-animated {
          animation: dataPointPulse 1.5s ease-in-out infinite;
        }
        @keyframes flowingGradient {
          0% {
            stop-opacity: 0.3;
          }
          50% {
            stop-opacity: 0.6;
          }
          100% {
            stop-opacity: 0.3;
          }
        }
        .gradient-animated stop {
          animation: flowingGradient 2s ease-in-out infinite;
        }
      `}</style>

      {/* Floating Gold Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full opacity-30 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${6 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-stone-950/95 backdrop-blur-md shadow-2xl shadow-amber-900/10' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-500 blur-lg opacity-50"></div>
                <img src="https://static.readdy.ai/image/e468a762cbf2c4de3f207872d8ecdaa7/0b3887cade0bf729b27dae59d525fd13.png" alt="Rick Club Investment" className="h-12 w-auto relative" />
              </div>
              <span className={`text-xl font-bold font-sans ${isScrolled ? 'text-white' : 'text-white'}`}>Rick Club Investment</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`font-medium transition-all whitespace-nowrap hover:text-amber-400 ${isScrolled ? 'text-gray-300' : 'text-white/90'}`}>{t.nav.home}</a>
              <a href="#services" className={`font-medium transition-all whitespace-nowrap hover:text-amber-400 ${isScrolled ? 'text-gray-300' : 'text-white/90'}`}>{t.nav.services}</a>
              <a href="#plans" className={`font-medium transition-all whitespace-nowrap hover:text-amber-400 ${isScrolled ? 'text-gray-300' : 'text-white/90'}`}>{t.nav.investment}</a>
              <a href="#about" className={`font-medium transition-all whitespace-nowrap hover:text-amber-400 ${isScrolled ? 'text-gray-300' : 'text-white/90'}`}>{t.nav.about}</a>
              <a href="#faq" className={`font-medium transition-all whitespace-nowrap hover:text-amber-400 ${isScrolled ? 'text-gray-300' : 'text-white/90'}`}>{t.nav.faq}</a>
              
              {/* Language Toggle */}
              <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-lg p-1">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    language === 'en'
                      ? 'bg-amber-400 text-stone-900'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  English
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    language === 'es'
                      ? 'bg-amber-400 text-stone-900'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Español
                </button>
              </div>
              
              <a href="#contact" className="relative group cursor-pointer">
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-yellow-400/20 rounded-lg blur opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <span className="relative bg-gradient-to-r from-amber-500/20 to-yellow-400/20 text-stone-900 px-6 py-2.5 rounded-lg font-bold text-lg flex items-center space-x-2 whitespace-nowrap">
                  {t.nav.getStarted}
                </span>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e90f13acb38947e6ce81ac229961c14d.jpg" 
            alt="Luxury Investment" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/90 via-stone-900/70 to-stone-950/80"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-transparent to-transparent"></div>
        </div>
        
        {/* Animated Grid Lines */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(to right, #d4af37 1px, transparent 1px), linear-gradient(to bottom, #d4af37 1px, transparent 1px)',
            backgroundSize: '100px 100px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur-sm border border-amber-400/30 px-5 py-2.5 rounded-full mb-8">
                <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                <span className="text-amber-300 text-sm font-semibold tracking-wider">{t.hero.badge}</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight font-sans">
                <span className="text-white">{t.hero.title1}</span><br />
                <span className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 bg-clip-text text-transparent">{t.hero.title2}</span><br />
                <span className="text-white">{t.hero.title3}</span>
              </h1>
              <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-xl">
                {t.hero.description}
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                <a href="#contact" className="relative group cursor-pointer">
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-xl blur-lg opacity-75 group-hover:opacity-100 transition-all group-hover:blur-xl"></div>
                  <span className="relative bg-gradient-to-r from-amber-500 to-yellow-400 text-stone-900 px-10 py-4 rounded-xl font-bold text-lg flex items-center space-x-2 whitespace-nowrap">
                    <span>{t.hero.startInvesting}</span>
                    <i className="ri-arrow-right-up-line text-xl"></i>
                  </span>
                </a>
                <a href="#plans" className="border-2 border-amber-400/50 text-amber-400 px-10 py-4 rounded-xl font-semibold hover:bg-amber-400/10 transition-all whitespace-nowrap flex items-center space-x-2">
                  <i className="ri-play-circle-line text-xl"></i>
                  <span>{t.hero.seeReturns}</span>
                </a>
              </div>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-8">
                <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm px-4 py-3 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-lg flex items-center justify-center">
                    <i className="ri-shield-check-fill text-stone-900 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">{t.hero.securityLabel}</div>
                    <div className="text-sm font-semibold text-white">{t.hero.securityValue}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3 bg-white/5 backdrop-blur-sm px-4 py-3 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-green-500 rounded-lg flex items-center justify-center">
                    <i className="ri-home-4-fill text-stone-900 text-lg"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">{t.hero.assetsLabel}</div>
                    <div className="text-sm font-semibold text-white">{t.hero.assetsValue}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Hero Stats Card */}
            <div className="relative hidden lg:block">
              <div className="relative">
                {/* Main Stats Card */}
                <div className="bg-gradient-to-br from-stone-900/90 to-stone-800/90 backdrop-blur-xl rounded-3xl p-8 border border-amber-500/20 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                    <div>
                      <div className="text-gray-400 text-sm mb-1">{t.hero.portfolioGrowth}</div>
                      <div className="text-3xl font-bold text-white font-sans">{t.hero.investmentReturns}</div>
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center">
                      <i className="ri-line-chart-fill text-stone-900 text-2xl"></i>
                    </div>
                  </div>
                  
                  {/* Mini Chart */}
                  <div className="relative h-48 mb-6">
                    <svg className="w-full h-full" viewBox="0 0 400 200">
                      {/* Grid Lines */}
                      <defs>
                        <linearGradient id="goldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#d4af37" stopOpacity="0.5"/>
                          <stop offset="100%" stopColor="#d4af37" stopOpacity="0"/>
                        </linearGradient>
                        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#fbbf24"/>
                          <stop offset="50%" stopColor="#d4af37"/>
                          <stop offset="100%" stopColor="#f59e0b"/>
                        </linearGradient>
                      </defs>
                      
                      {/* Grid */}
                      {[0, 50, 100, 150].map((y) => (
                        <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#374151" strokeWidth="1" strokeDasharray="5,5" opacity="0.3"/>
                      ))}
                      
                      {/* Area Fill */}
                      <path
                        d="M0,180 Q50,170 100,150 T200,100 T300,60 T400,20 L400,200 L0,200 Z"
                        fill="url(#goldGradient)"
                        className="animate-pulse"
                      />
                      
                      {/* Main Line - Rick Club - Animated */}
                      <path
                        d="M0,180 Q50,170 100,150 T200,100 T300,60 T400,20"
                        fill="none"
                        stroke="url(#lineGradient)"
                        strokeWidth="4"
                        strokeLinecap="round"
                        className="animated-line-gold"
                      />
                      
                      {/* Bank Line - Animated */}
                      <path
                        d="M0,180 Q100,178 200,175 T400,165"
                        fill="none"
                        stroke="#6b7280"
                        strokeWidth="2"
                        opacity="0.6"
                        className="animated-line-gray"
                      />
                      
                      {/* Data Points */}
                      <circle cx="100" cy="150" r="6" fill="#d4af37" className="data-point-animated"/>
                      <circle cx="200" cy="100" r="6" fill="#d4af37" className="data-point-animated" style={{ animationDelay: '0.3s' }}/>
                      <circle cx="300" cy="60" r="6" fill="#d4af37" className="data-point-animated" style={{ animationDelay: '0.6s' }}/>
                      <circle cx="400" cy="20" r="8" fill="#fbbf24" stroke="#fff" strokeWidth="2"/>
                    </svg>
                    
                    {/* Labels */}
                    <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-500 px-2">
                      <span>{t.hero.year} 1</span>
                      <span>{t.hero.year} 2</span>
                      <span>{t.hero.year} 3</span>
                      <span>{t.hero.year} 4</span>
                      <span>{t.hero.year} 5</span>
                    </div>
                  </div>
                  
                  {/* Legend */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full"></div>
                        <span className="text-sm text-gray-400">{t.hero.rickClub}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                        <span className="text-sm text-gray-400">{t.hero.bank}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Badge - Animated on Hover */}
                <div className="absolute -top-6 -right-6 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl p-5 shadow-2xl transform rotate-6 cursor-pointer badge-hover">
                  <div className="text-4xl font-bold text-stone-900 font-sans">20%</div>
                  <div className="text-sm text-stone-700 font-medium">{t.hero.annualReturns}</div>
                </div>
                
                {/* Bottom Badge - Animated on Hover */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-5 shadow-2xl transform -rotate-3 cursor-pointer badge-hover-left">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <i className="ri-arrow-up-line text-emerald-600 text-xl"></i>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-stone-900 font-sans">$2M+</div>
                      <div className="text-xs text-gray-500">{t.hero.assetsManaged}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center space-y-2 animate-bounce">
          <span className="text-amber-400 text-xs tracking-widest">{t.hero.scroll}</span>
          <i className="ri-arrow-down-line text-amber-400 text-xl"></i>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={countersRef} className="py-16 bg-stone-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-amber-500/5"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent font-sans mb-2">
                <AnimatedCounter end={20} suffix="%" />
              </div>
              <div className="text-gray-400 text-sm">{t.stats.annualReturns}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent font-sans mb-2">
                <AnimatedCounter end={0} suffix="%" />
              </div>
              <div className="text-gray-400 text-sm">{t.stats.marketRisk}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent font-sans mb-2">
                <AnimatedCounter end={100} suffix="%" />
              </div>
              <div className="text-gray-400 text-sm">{t.stats.notarySecured}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent font-sans mb-2">
                <AnimatedCounter end={2} prefix="$" suffix="M+" />
              </div>
              <div className="text-gray-400 text-sm">{t.stats.assetsManaged}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-stone-50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-amber-200/30 to-yellow-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-emerald-200/30 to-green-200/30 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-yellow-100 px-5 py-2 rounded-full mb-6">
              <i className="ri-sparkling-fill text-amber-600"></i>
              <span className="text-amber-700 text-sm font-semibold tracking-wider">{t.services.badge}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-6 font-sans">{t.services.title} <span className="bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent">{t.services.titleHighlight}</span> {t.services.titleEnd}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t.services.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-pointer border border-gray-100 hover:border-amber-200">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
                  <i className="ri-home-4-fill text-3xl text-stone-900"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-4 font-sans">{t.services.realEstate.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.services.realEstate.description}
              </p>
            </div>

            <div className="group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-pointer border border-gray-100 hover:border-emerald-200">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-green-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-emerald-400 to-green-500 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
                  <i className="ri-percent-fill text-3xl text-white"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-4 font-sans">{t.services.returns.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.services.returns.description}
              </p>
            </div>

            <div className="group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-pointer border border-gray-100 hover:border-amber-200">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-stone-700 to-stone-900 rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity"></div>
                <div className="relative w-16 h-16 bg-gradient-to-br from-stone-700 to-stone-900 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform">
                  <i className="ri-shield-check-fill text-3xl text-amber-400"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-stone-900 mb-4 font-sans">{t.services.zeroRisk.title}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t.services.zeroRisk.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Comparison Section */}
      <section id="plans" className="py-24 bg-stone-950 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 px-5 py-2 rounded-full mb-6">
              <i className="ri-bar-chart-grouped-fill text-amber-400"></i>
              <span className="text-amber-300 text-sm font-semibold tracking-wider">{t.comparison.badge}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-sans">
              {t.comparison.title}<span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">{t.comparison.titleHighlight}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {t.comparison.description}
            </p>
          </div>

          {/* Main Comparison Chart */}
          <div className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-3xl p-8 md:p-12">
            {/* Chart - Now at the top */}
            <div className="relative max-w-4xl mx-auto mb-12">
              <h3 className="text-xl font-bold text-white mb-8 font-sans text-center">{t.comparison.chartTitle}</h3>
              <div className="relative h-80">
                <svg className="w-full h-full" viewBox="0 0 600 300">
                  <defs>
                    <linearGradient id="chartGoldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#d4af37" stopOpacity="0.4"/>
                      <stop offset="100%" stopColor="#d4af37" stopOpacity="0"/>
                    </linearGradient>
                    <linearGradient id="chartGrayGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#6b7280" stopOpacity="0.2"/>
                      <stop offset="100%" stopColor="#6b7280" stopOpacity="0"/>
                    </linearGradient>
                    <linearGradient id="chartLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#fbbf24"/>
                      <stop offset="50%" stopColor="#d4af37"/>
                      <stop offset="100%" stopColor="#f59e0b"/>
                    </linearGradient>
                  </defs>
                  
                  {/* Y-axis labels */}
                  <text x="10" y="30" fill="#9ca3af" fontSize="12">$2M</text>
                  <text x="10" y="90" fill="#9ca3af" fontSize="12">$1.5M</text>
                  <text x="10" y="150" fill="#9ca3af" fontSize="12">$1M</text>
                  <text x="10" y="210" fill="#9ca3af" fontSize="12">$500K</text>
                  <text x="10" y="270" fill="#9ca3af" fontSize="12">$0</text>
                  
                  {/* Grid lines */}
                  {[30, 90, 150, 210, 270].map((y) => (
                    <line key={y} x1="60" y1={y} x2="580" y2={y} stroke="#374151" strokeWidth="1" strokeDasharray="5,5" opacity="0.3"/>
                  ))}
                  
                  {/* Bank Area */}
                  <path
                    d="M60,270 L164,265 L268,260 L372,255 L476,250 L580,245 L580,270 L60,270 Z"
                    fill="url(#chartGrayGradient)"
                  />
                  
                  {/* Rick Club Area */}
                  <path
                    d="M60,270 L164,220 L268,170 L372,120 L476,70 L580,30 L580,270 L60,270 Z"
                    fill="url(#chartGoldGradient)"
                    className="animate-pulse"
                  />
                  
                  {/* Bank Line - Animated */}
                  <path
                    d="M60,270 L164,265 L268,260 L372,255 L476,250 L580,245"
                    fill="none"
                    stroke="#6b7280"
                    strokeWidth="3"
                    strokeLinecap="round"
                    className="animated-line-gray"
                  />
                  
                  {/* Rick Club Line - Animated */}
                  <path
                    d="M60,270 L164,220 L268,170 L372,120 L476,70 L580,30"
                    fill="none"
                    stroke="url(#chartLineGradient)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    className="animated-line-gold"
                  />
                  
                  {/* Data Points - Rick Club - Animated */}
                  <circle cx="60" cy="270" r="6" fill="#d4af37" className="data-point-animated"/>
                  <circle cx="164" cy="220" r="6" fill="#d4af37" className="data-point-animated" style={{ animationDelay: '0.2s' }}/>
                  <circle cx="268" cy="170" r="6" fill="#d4af37" className="data-point-animated" style={{ animationDelay: '0.4s' }}/>
                  <circle cx="372" cy="120" r="6" fill="#d4af37" className="data-point-animated" style={{ animationDelay: '0.6s' }}/>
                  <circle cx="476" cy="70" r="6" fill="#d4af37" className="data-point-animated" style={{ animationDelay: '0.8s' }}/>
                  <circle cx="580" cy="30" r="8" fill="#fbbf24" stroke="#fff" strokeWidth="2"/>
                  
                  {/* X-axis labels */}
                  <text x="60" y="295" fill="#9ca3af" fontSize="12" textAnchor="middle">Start</text>
                  <text x="164" y="295" fill="#9ca3af" fontSize="12" textAnchor="middle">Year 1</text>
                  <text x="268" y="295" fill="#9ca3af" fontSize="12" textAnchor="middle">Year 2</text>
                  <text x="372" y="295" fill="#9ca3af" fontSize="12" textAnchor="middle">Year 3</text>
                  <text x="476" y="295" fill="#9ca3af" fontSize="12" textAnchor="middle">Year 4</text>
                  <text x="580" y="295" fill="#9ca3af" fontSize="12" textAnchor="middle">Year 5</text>
                </svg>
              </div>
              
              {/* Legend */}
              <div className="flex items-center justify-center space-x-8 mt-6">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full"></div>
                  <span className="text-gray-500 text-sm">Rick Club (20%)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-gray-500 rounded-full"></div>
                  <span className="text-gray-500 text-sm">Traditional Bank (1.5%)</span>
                </div>
              </div>
            </div>
            
            {/* Comparison Cards - Now in a row at the bottom */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-stone-800/50 rounded-2xl p-6 border border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 text-sm">{t.comparison.example} #1</span>
                  <i className="ri-bank-fill text-gray-600"></i>
                </div>
                <div className="text-2xl font-bold text-gray-400 mb-1 font-sans">$1,000,000</div>
                <div className="text-sm text-gray-500 mb-4">{t.comparison.bankFor5Years}</div>
                <div className="border-t border-gray-800 pt-4">
                  <div className="text-3xl font-bold text-gray-400 font-sans">$75,000</div>
                  <div className="text-xs text-gray-600">{t.comparison.totalEarnings}</div>
                </div>
                <button
                  onClick={() => setIsVideoModalOpen(true)}
                  className="mt-4 w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 hover:from-amber-500/30 hover:to-yellow-500/30 border border-amber-500/30 text-amber-400 px-4 py-2.5 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-fill text-lg"></i>
                  <span>{t.comparison.watchVideo}</span>
                </button>
              </div>
              
              <div className="bg-stone-900/50 rounded-2xl p-6 border border-gray-800 hover:border-gray-700 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-500 text-sm">{t.comparison.example} #2</span>
                  <i className="ri-bank-fill text-gray-600"></i>
                </div>
                <div className="text-2xl font-bold text-white mb-1 font-sans">$100,000</div>
                <div className="text-sm text-gray-500 mb-4">{t.comparison.bankFor5Years}</div>
                <div className="border-t border-gray-800 pt-4">
                  <div className="text-3xl font-bold text-gray-400 font-sans">$7,500</div>
                  <div className="text-xs text-gray-600">{t.comparison.totalEarnings}</div>
                </div>
                <button
                  onClick={() => setIsVideoModal2Open(true)}
                  className="mt-4 w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 hover:from-amber-500/30 hover:to-yellow-500/30 border border-amber-500/30 text-amber-400 px-4 py-2.5 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-fill text-lg"></i>
                  <span>{t.comparison.watchVideo}</span>
                </button>
              </div>

              <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/5 rounded-2xl p-6 border border-amber-500/30 relative overflow-hidden">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-400/20 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-amber-400 text-sm font-semibold">Rick Club</span>
                    <i className="ri-vip-crown-fill text-amber-400"></i>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1 font-sans">$100,000</div>
                  <div className="text-sm text-amber-300/70 mb-4">{t.comparison.rickClubFor5Years}</div>
                  <div className="border-t border-amber-500/30 pt-4">
                    <div className="text-3xl font-bold bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent font-sans">$200,000</div>
                    <div className="text-xs text-amber-400">{t.comparison.doubleYourMoney}</div>
                  </div>
                </div>
                <button
                  onClick={() => setIsVideoModal3Open(true)}
                  className="mt-4 w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 hover:from-amber-500/30 hover:to-yellow-500/30 border border-amber-500/30 text-amber-400 px-4 py-2.5 rounded-xl font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-fill text-lg"></i>
                  <span>{t.comparison.watchVideo}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-50 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-yellow-100 px-5 py-2 rounded-full mb-6">
                <i className="ri-award-fill text-amber-600"></i>
                <span className="text-amber-700 text-sm font-semibold tracking-wider">{t.about.badge}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-8 font-sans leading-tight">
                {t.about.title}<span className="bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent">{t.about.titleHighlight}</span><br />
                {t.about.titleEnd}
              </h2>
              <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                <p>
                  <strong className="text-stone-900">{t.about.paragraph1}</strong>
                </p>
                <p>
                  {t.about.paragraph2}
                </p>
                <p>
                  {t.about.paragraph3}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-6 mt-10">
                <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl">
                  <div className="text-3xl font-bold bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent font-sans">0%</div>
                  <div className="text-xs text-gray-500 mt-1">{t.about.stockMarketRisk}</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl">
                  <div className="text-3xl font-bold text-emerald-600 font-sans">100%</div>
                  <div className="text-xs text-gray-500 mt-1">{t.about.notarySecured}</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl">
                  <div className="text-3xl font-bold bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent font-sans">20%+</div>
                  <div className="text-xs text-gray-500 mt-1">{t.about.annualReturns}</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform">
                    <div className="w-full h-64 rounded-2xl overflow-hidden shadow-2xl">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Luxury%20real%20estate%20investment%20portfolio%20presentation%20with%20financial%20growth%20charts%20and%20premium%20property%20images%2C%20professional%20wealth%20management%20consultation%2C%20modern%20office%20setting%20with%20elegant%20decor%2C%20gold%20and%20black%20color%20scheme%2C%20sophisticated%20business%20atmosphere%2C%20high-end%20investment%20advisory%20meeting%2C%20detailed%20financial%20documents%20and%20property%20blueprints&width=400&height=300&seq=about-investment-portfolio-v1&orientation=landscape" 
                        alt="Investment Portfolio" 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                  <div className="rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform">
                    <div className="w-full h-48 rounded-2xl overflow-hidden shadow-2xl">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Official%20notary%20seal%20and%20legal%20documents%20for%20real%20estate%20investment%20on%20mahogany%20desk%2C%20professional%20notarization%20process%20with%20gold%20pen%2C%20legal%20security%20and%20trust%2C%20official%20stamps%20and%20signatures%2C%20luxury%20office%20setting%20with%20warm%20lighting%2C%20secure%20investment%20paperwork%2C%20high-quality%20detailed%20photography&width=400&height=300&seq=about-notary-v2&orientation=landscape" 
                        alt="Notary Security" 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform">
                    <div className="w-full h-56 rounded-2xl overflow-hidden shadow-2xl">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Luxury%20modern%20mansion%20exterior%20with%20pristine%20landscaping%20and%20palm%20trees%2C%20premium%20real%20estate%20investment%20property%2C%20architectural%20excellence%20with%20Mediterranean%20style%2C%20professional%20real%20estate%20photography%2C%20golden%20hour%20lighting%2C%20investment-grade%20property%2C%20high-quality%20construction%2C%20sophisticated%20design%20elements&width=400&height=350&seq=about-property-v2&orientation=portrait" 
                        alt="Property Investment" 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                  <div className="rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform">
                    <div className="w-full h-52 rounded-2xl overflow-hidden shadow-2xl">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20premium%20residential%20land%20development%20site%20with%20luxury%20homes%20in%20background%2C%20pristine%20real%20estate%20investment%20opportunity%2C%20premium%20location%20for%20construction%2C%20professional%20drone%20photography%2C%20clear%20blue%20sky%2C%20investment-grade%20land%20parcel%2C%20development%20potential%2C%20natural%20landscape%20with%20modern%20elements&width=400&height=400&seq=about-land-v2&orientation=squarish" 
                        alt="Land Investment" 
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl px-8 py-4 shadow-2xl flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center">
                  <i className="ri-trophy-fill text-stone-900 text-xl"></i>
                </div>
                <div>
                  <div className="text-white font-bold">{t.about.trustedBy}</div>
                  <div className="text-gray-400 text-sm">{t.about.since}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Section */}
      <section className="py-24 bg-gradient-to-br from-stone-900 via-stone-950 to-stone-900 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-400/5 rounded-full blur-3xl"></div>
        </div>
        
        {/* Decorative Grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(to right, #d4af37 1px, transparent 1px), linear-gradient(to bottom, #d4af37 1px, transparent 1px)',
            backgroundSize: '80px 80px'
          }}></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 px-5 py-2 rounded-full mb-8">
              <i className="ri-lightbulb-flash-fill text-amber-400"></i>
              <span className="text-amber-300 text-sm font-semibold tracking-wider">{t.future.badge}</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 font-sans leading-tight">
              {t.future.title} <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">{t.future.titleHighlight}</span> {t.future.titleEnd}
            </h2>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <div className="relative mb-8">
                  <div className="w-full h-64 rounded-2xl overflow-hidden shadow-2xl bg-stone-800">
                    <iframe
                      src="https://www.youtube.com/embed/SrMJGLiOpdg"
                      title="Rick Club Investment Video"
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    ></iframe>
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl p-4 shadow-xl">
                    <i className="ri-key-2-fill text-3xl text-stone-900"></i>
                  </div>
                </div>
              </div>
              
              <div className="text-gray-300 space-y-6">
                <p className="text-lg leading-relaxed">
                  {t.future.paragraph1}
                </p>
                <p className="text-lg leading-relaxed">
                  {t.future.paragraph2}
                </p>
                <p className="text-lg leading-relaxed">
                  {t.future.paragraph3}
                </p>
              </div>
            </div>

            <div className="mt-12 pt-10 border-t border-white/10">
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-bold text-white font-sans">
                  {t.future.straightToPoint}
                </h3>
                <p className="text-gray-400 mt-2">{t.future.comparisonDescription}</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-gray-700 rounded-xl flex items-center justify-center">
                      <i className="ri-bank-fill text-gray-400 text-xl"></i>
                    </div>
                    <div>
                      <div className="text-white font-bold">{t.future.traditionalBank}</div>
                      <div className="text-gray-500 text-sm">{t.future.annualReturns}</div>
                    </div>
                  </div>
                  <ul className="space-y-3 text-gray-400">
                    <li className="flex items-center space-x-2">
                      <i className="ri-close-circle-fill text-red-400"></i>
                      <span>{t.future.minimalGrowth}</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <i className="ri-close-circle-fill text-red-400"></i>
                      <span>{t.future.yearsForReturns}</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <i className="ri-close-circle-fill text-red-400"></i>
                      <span>{t.future.inflationLoss}</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/5 rounded-2xl p-6 border border-amber-500/30 relative overflow-hidden">
                  <div className="absolute -top-6 -right-6 w-24 h-24 bg-amber-400/20 rounded-full blur-2xl"></div>
                  <div className="relative">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center">
                        <i className="ri-vip-crown-fill text-stone-900 text-xl"></i>
                      </div>
                      <div>
                        <div className="text-white font-bold">{t.future.rickClubInvestment}</div>
                        <div className="text-amber-400 text-sm font-semibold">{t.future.annualReturns}</div>
                      </div>
                    </div>
                    <ul className="space-y-3 text-gray-300">
                      <li className="flex items-center space-x-2">
                        <i className="ri-checkbox-circle-fill text-emerald-400"></i>
                        <span>{t.future.doubleIn5Years}</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <i className="ri-checkbox-circle-fill text-emerald-400"></i>
                        <span>{t.future.physicalAssets}</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <i className="ri-checkbox-circle-fill text-emerald-400"></i>
                        <span>{t.future.notarySecured}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="text-center mt-10">
                <a href="#contact" className="relative group inline-block cursor-pointer">
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-xl blur-lg opacity-75 group-hover:opacity-100 transition-all"></div>
                  <span className="relative bg-gradient-to-r from-amber-500 to-yellow-400 text-stone-900 px-10 py-4 rounded-xl font-bold text-lg flex items-center space-x-2 whitespace-nowrap">
                    <span>{t.future.takeControl}</span>
                    <i className="ri-arrow-right-up-line text-xl"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-stone-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-100 to-yellow-100 px-5 py-2 rounded-full mb-6">
              <i className="ri-question-answer-fill text-amber-600"></i>
              <span className="text-amber-700 text-sm font-semibold tracking-wider">{t.faq.badge}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4 font-sans">{t.faq.title} <span className="bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent">{t.faq.titleHighlight}</span></h2>
            <p className="text-xl text-gray-600">{t.faq.description}</p>
          </div>

          <div className="space-y-4">
            {t.faq.questions.map((faq, index) => (
              <div 
                key={index}
                className={`rounded-2xl overflow-hidden transition-all duration-300 ${
                  index === 0 
                    ? 'bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border-2 border-amber-300 shadow-lg shadow-amber-100' 
                    : 'bg-white border-2 border-gray-200 hover:border-amber-200 hover:shadow-lg'
                }`}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer"
                >
                  <span className={`text-lg font-bold ${index === 0 ? 'text-stone-900' : 'text-stone-800'}`}>
                    {faq.q}
                  </span>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                    openFaq === index 
                      ? 'bg-gradient-to-br from-amber-400 to-yellow-500 rotate-180' 
                      : index === 0 ? 'bg-amber-200' : 'bg-gray-100'
                  }`}>
                    <i className={`ri-arrow-down-s-line text-xl ${openFaq === index ? 'text-stone-900' : 'text-gray-600'}`}></i>
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-8 pb-6">
                    <div className={`border-l-4 ${index === 0 ? 'border-amber-400' : 'border-amber-300'} pl-6`}>
                      <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-stone-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <div className="text-white">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 px-5 py-2 rounded-full mb-6">
                <i className="ri-chat-smile-3-fill text-amber-400"></i>
                <span className="text-amber-300 text-sm font-semibold tracking-wider">{t.contact.badge}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-8 font-sans leading-tight">
                {t.contact.title}<br />
                <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">{t.contact.titleHighlight}</span>
              </h2>
              <p className="text-xl text-gray-300 mb-12 leading-relaxed">
                {t.contact.description}
              </p>

              <div className="space-y-4">
                <div className="flex items-center space-x-4 bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-amber-500/30 transition-colors cursor-pointer">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-fill text-2xl text-stone-900"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t.contact.phone}</div>
                    <div className="text-lg font-semibold">(214) 815-4829</div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-amber-500/30 transition-colors cursor-pointer">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-fill text-2xl text-stone-900"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t.contact.email}</div>
                    <div className="text-lg font-semibold">rickclub0516@gmail.com</div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:border-amber-500/30 transition-colors cursor-pointer">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-fill text-2xl text-stone-900"></i>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">{t.contact.officeLocation}</div>
                    <div className="text-lg font-semibold">{t.contact.location}</div>
                  </div>
                </div>
              </div>

              {/* Google Map */}
              <div className="mt-8 rounded-2xl overflow-hidden border border-white/10">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3353.8876!2d-96.9584!3d32.8308!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e82a5c5555555%3A0x0!2s320%20Deker%20Dr%2C%20Irving%2C%20TX%2075062!5e0!3m2!1sen!2sus!4v1704067200000!5m2!1sen!2sus"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                  className="w-full"
                ></iframe>
              </div>

              <div className="flex space-x-3 mt-8">
                <a href="#" className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-amber-400 hover:to-yellow-500 hover:text-stone-900 transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://www.youtube.com/channel/UChIapXTqOlmbUOI1xbpgl9g" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-amber-400 hover:to-yellow-500 hover:text-stone-900 transition-all cursor-pointer">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-amber-400 hover:to-yellow-500 hover:text-stone-900 transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-amber-400 hover:to-yellow-500 hover:text-stone-900 transition-all cursor-pointer">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-stone-900 mb-2 font-sans">{t.contact.scheduleConsultation}</h3>
              <p className="text-gray-500 mb-6">{t.contact.formDescription}</p>
              
              {formStatus === 'success' && (
                <div className="mb-6 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl">
                  <div className="flex items-center">
                    <i className="ri-checkbox-circle-fill text-xl mr-2"></i>
                    <span>{t.contact.successMessage}</span>
                  </div>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl">
                  <div className="flex items-center">
                    <i className="ri-error-warning-fill text-xl mr-2"></i>
                    <span>{t.contact.errorMessage}</span>
                  </div>
                </div>
              )}

              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">{t.contact.fullName}</label>
                  <input 
                    type="text" 
                    name="fullName"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:outline-none transition-colors disabled:bg-gray-100 text-sm" 
                    placeholder={t.contact.fullNamePlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">{t.contact.email}</label>
                  <input 
                    type="email" 
                    name="email"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:outline-none transition-colors disabled:bg-gray-100 text-sm" 
                    placeholder={t.contact.emailPlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">{t.contact.phone}</label>
                  <input 
                    type="tel" 
                    name="phone"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:outline-none transition-colors disabled:bg-gray-100 text-sm" 
                    placeholder={t.contact.phonePlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">{t.contact.investmentAmount}</label>
                  <select 
                    name="investmentAmount"
                    required
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:outline-none transition-colors disabled:bg-gray-100 text-sm cursor-pointer"
                  >
                    <option value="">{t.contact.selectAmount}</option>
                    <option value="50k-100k">$50,000 - $100,000</option>
                    <option value="100k-250k">$100,000 - $250,000</option>
                    <option value="250k-500k">$250,000 - $500,000</option>
                    <option value="500k+">$500,000+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">{t.contact.message}</label>
                  <textarea 
                    name="message"
                    maxLength={500}
                    rows={4}
                    disabled={formStatus === 'submitting'}
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:outline-none transition-colors resize-none disabled:bg-gray-100 text-sm" 
                    placeholder={t.contact.messagePlaceholder}
                  ></textarea>
                  <div className="text-xs text-gray-400 mt-1">{t.contact.maxCharacters}</div>
                </div>
                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full relative group cursor-pointer"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-xl blur opacity-75 group-hover:opacity-100 transition-opacity"></div>
                  <span className="relative bg-gradient-to-r from-amber-500 to-yellow-400 text-stone-900 py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-2 whitespace-nowrap disabled:opacity-50">
                    <span>{formStatus === 'submitting' ? t.contact.submitting : t.contact.submitButton}</span>
                    {formStatus !== 'submitting' && <i className="ri-arrow-right-line"></i>}
                  </span>
                </button>
                <p className="text-xs text-gray-400 text-center">
                  {t.contact.privacyNotice}
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-gray-300 py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #d4af37 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-yellow-500 blur-lg opacity-50"></div>
                  <img src="https://static.readdy.ai/image/e468a762cbf2c4de3f207872d8ecdaa7/0b3887cade0bf729b27dae59d525fd13.png" alt="Rick Club Investment" className="h-10 w-auto relative" />
                </div>
                <span className="text-xl font-bold text-white font-sans">Rick Club</span>
              </div>
              <p className="text-sm mb-6 leading-relaxed text-gray-400">
                {t.footer.description}
              </p>
              <div className="flex space-x-3">
                <a href="#" className="w-10 h-10 border border-gray-700 rounded-lg flex items-center justify-center hover:border-amber-400 hover:text-amber-400 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="https://www.youtube.com/channel/UChIapXTqOlmbUOI1xbpgl9g" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-700 rounded-lg flex items-center justify-center hover:border-amber-400 hover:text-amber-400 transition-colors cursor-pointer">
                  <i className="ri-youtube-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-gray-700 rounded-lg flex items-center justify-center hover:border-amber-400 hover:text-amber-400 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-gray-700 rounded-lg flex items-center justify-center hover:border-amber-400 hover:text-amber-400 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4 font-sans">{t.footer.company}</h4>
              <ul className="space-y-3">
                <li><a href="#about" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.aboutUs}</a></li>
                <li><a href="#services" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.ourServices}</a></li>
                <li><a href="#plans" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.investmentPlans}</a></li>
                <li><a href="#faq" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.faq}</a></li>
                <li><a href="#contact" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.contact}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4 font-sans">{t.footer.services}</h4>
              <ul className="space-y-3">
                <li><a href="#" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.realEstateInvestment}</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.landAcquisition}</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.propertyManagement}</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.wealthCreation}</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.financialAdvisory}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4 font-sans">{t.footer.legal}</h4>
              <ul className="space-y-3">
                <li><Link to="/privacy-policy" className="hover:text-amber-400 transition-colors cursor-pointer">{t.footer.privacyPolicy}</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
              <div className="text-sm text-gray-500">
                {t.footer.copyright}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
